// DemoModal.js
import React, { useState, useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { PDFViewer, pdf } from "@react-pdf/renderer";
import { useNavigate } from "react-router-dom";
import { useDemo } from "../../contexts/DemoContext";
import MeetingDebriefingRoom from "../domains/advisor-portal/MeetingDebriefingRoom";
import CallConfirmationModal from "../domains/advisor-portal/CallConfirmationModal";
import RSUSummaryPDF from "./RSUSummaryPDF";
import Modal from "./Modal";
import { useTheme } from "../../contexts/ThemeContext";
import { MODAL_TYPES, MODAL_TEMPLATES } from '../../demo/annualReviewDemoLogic';
import { ThemeProvider } from "styled-components";
import { demoMeetingDetails } from "../../demo/briefingRoomData";

// Add new ModalWrapper styled component
const ModalWrapper = styled.div`
  margin: 0;

  @media (min-width: 1024px) {
    margin: 0;
  }
`;

// Keep necessary styled components
const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const OpenSummaryButton = styled.button`
  background: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.1)' : theme.colors.surface.paper};
  border: 1px solid ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.2)' : theme.colors.neutral[200]};
  color: ${({ theme }) => theme.isDarkMode ? '#FFFFFF' : theme.colors.neutral[900]};
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s;
  &:hover {
    background: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.2)' : theme.colors.surface.hover};
    border-color: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.3)' : theme.colors.neutral[300]};
  }
`;

// ——— Styled Components ———

// Remove ModalOverlay and ModalContent styled components as we'll use Modal

const ReportBody = styled.div`
  background: ${({ theme }) => theme.isDarkMode ? '#1E1E1E' : '#FFFFFF'};
  border-radius: 10px;
  padding: 1rem 1.25rem;
  color: ${({ theme }) => theme.isDarkMode ? '#FFFFFF' : theme.colors.neutral[900]};
  font-family: "Inter", sans-serif;
  line-height: 1.4;
  letter-spacing: 0.005em;
  white-space: normal;
  border: 1px solid ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.1)' : theme.colors.neutral[200]};
  position: relative;
  pointer-events: ${props => props.nonInteractive ? 'none' : 'auto'};
  font-size: 0.92rem;

  /* Style adjustments for non-interactive state */
  ${props => props.nonInteractive && `
    &:hover {
      cursor: default;
    }
    
    /* Prevent interaction with links and buttons while allowing text selection */
    a, button {
      pointer-events: none;
      cursor: default;
    }
    
    /* Keep text selectable */
    * {
      user-select: text;
    }
  `}

  h1, h2, h3, h4 {
    margin-top: 1rem;
    margin-bottom: 0.5rem;
    color: ${({ theme }) => theme.isDarkMode ? '#FFFFFF' : theme.colors.neutral[900]};
    line-height: 1.3;
    font-weight: 600;
  }
  h1 { font-size: 1.25rem; }
  h2 { font-size: 1.13rem; margin-top: 1.5rem; }
  h3 { font-size: 1.08rem; margin-top: 1.25rem; }
  h4 { font-size: 1.05rem; margin-top: 1rem; }

  .document-link {
    font-size: 0.85rem;
    color: ${({ theme }) => theme.isDarkMode ? '#FFFFFF' : theme.colors.neutral[900]};
    opacity: 0.8;
    text-decoration: none;
    margin-left: 0.5rem;
    cursor: pointer;
    &:hover {
      opacity: 1;
      text-decoration: underline;
    }
  }

  .top-sections {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.5rem;
    margin-bottom: 1.5rem;
  }

  p {
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
    font-size: 0.92rem;
    color: ${({ theme }) => theme.isDarkMode ? '#FFFFFF' : theme.colors.neutral[900]};
  }

  ul, ol {
    margin: 0.5rem 0 0.5rem 1rem;
    padding-left: 0.5rem;
  }
  li {
    margin-bottom: 0.4rem;
    font-size: 0.92rem;
    color: ${({ theme }) => theme.isDarkMode ? '#FFFFFF' : theme.colors.neutral[900]};
  }
  li p {
    display: inline-block;
  }

  blockquote {
    margin: 0.75rem 0;
    padding: 0.6rem 0.8rem;
    border-left: 1px solid ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.2)' : theme.colors.neutral[200]};
    background: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.05)' : theme.colors.surface.paper};
    color: ${({ theme }) => theme.isDarkMode ? '#FFFFFF' : theme.colors.neutral[900]};
    font-style: italic;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    margin: 0.5rem 0;
    font-size: 0.85rem;
    color: ${({ theme }) => theme.isDarkMode ? '#FFFFFF' : theme.colors.neutral[900]};
  }
  thead {
    background: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.1)' : theme.colors.surface.paper};
  }
  th, td {
    border: 1px solid ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.1)' : theme.colors.neutral[200]};
    padding: 0.4rem 0.5rem;
    text-align: left;
    vertical-align: top;
  }
  th {
    font-weight: 600;
    color: ${({ theme }) => theme.isDarkMode ? '#FFFFFF' : theme.colors.neutral[900]};
  }
  tr:nth-child(even) {
    background: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.05)' : theme.colors.surface.paper};
  }
  tr:nth-child(odd) {
    background-color: transparent;
  }

  small {
    font-size: 0.85rem;
    opacity: 0.8;
    color: ${({ theme }) => theme.isDarkMode ? '#FFFFFF' : theme.colors.neutral[900]};
  }

  a {
    color: ${({ theme }) => theme.isDarkMode ? '#FFFFFF' : theme.colors.neutral[900]};
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }

  h2 a {
    font-size: 0.85rem;
    opacity: 0.8;
    margin-left: 0.5rem;
  }
`;

const ScrollWrapper = styled.div`
  max-height: 52vh;
  overflow-y: auto;
  position: relative;
  padding: 0.2rem 0.1rem;
  
  /* Enable scrollbar interaction */
  pointer-events: auto;

  /* Scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
    background: ${({ theme }) => theme.isDarkMode ? '#2A2A2A' : theme.colors.neutral[100]};
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.2)' : theme.colors.neutral[300]};
    border-radius: 4px;
    &:hover {
      background: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.3)' : theme.colors.neutral[400]};
    }
  }

  /* Visual scroll indicator */
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 24px;
    background: linear-gradient(transparent, ${({ theme }) => theme.isDarkMode ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.05)'});
    pointer-events: none;
    opacity: ${props => props.showScrollIndicator ? 1 : 0};
    transition: opacity 0.2s;
  }
`;

const Title = styled.h2`
  margin: 0 0 1rem;
  color: ${({ theme }) => theme.isDarkMode ? '#FFFFFF' : theme.colors.neutral[900]};
  font-size: 1.125rem;  // Reduced from 1.5rem to 1.125rem (18px)
  font-weight: 600;
`;

const Description = styled.p`
  margin: 0 0 1.5rem;
  color: ${({ theme }) => theme.isDarkMode ? '#FFFFFF' : theme.colors.neutral[900]};
  line-height: 1.5;
  font-size: 0.75rem;   // 12px
  font-style: ${(p) => (p.isItalic ? "italic" : "normal")};
  opacity: ${({ theme }) => theme.isDarkMode ? 0.9 : 1};
  white-space: pre-line;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.38rem;
  margin-top: 0.7rem;
`;

// Add new styled components for research modal
const ModalTopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  position: relative;
  margin-bottom: 0.2rem;
  min-height: 2.2rem;
`;

const TightReportBody = styled.div`
  background: ${({ theme }) => theme.isDarkMode ? '#1E1E1E' : '#FFFFFF'};
  border-radius: 8px;
  padding: 0.75rem 1rem;
  color: ${({ theme }) => theme.isDarkMode ? '#FFFFFF' : theme.colors.neutral[900]};
  font-size: 0.92rem;
  line-height: 1.35;
  font-family: "Inter", sans-serif;
  border: 1px solid ${({ theme }) => theme.isDarkMode ? 'rgba(255,255,255,0.09)' : theme.colors.neutral[200]};
  letter-spacing: 0.002em;
  white-space: normal;
  
  .top-sections {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.2rem;
    margin-bottom: 0.4rem;
  }
  
  .award-overview, .key-planning {
    font-size: 0.89rem;
    line-height: 1.3;
  }
  
  .award-overview {
    border-right: 1px solid ${({ theme }) => theme.isDarkMode ? 'rgba(255,255,255,0.05)' : theme.colors.neutral[100]};
    padding-right: 1rem;
  }
  
  .key-planning {
    padding-left: 1rem;
  }
  
  h1, h2, h3, h4 {
    margin: 0.5rem 0 0.2rem 0;
    font-size: 1.01rem;
    font-weight: 600;
    line-height: 1.15;
  }
  
  p {
    margin: 0.17rem 0 0.17rem 0;
    font-size: 0.92rem;
    line-height: 1.35;
  }
  
  ul, ol {
    margin: 0.23rem 0 0.17rem 1.1rem;
    padding-left: 0.3rem;
  }
  
  li { margin-bottom: 0.14rem; }
  
  table {
    width: 100%;
    border-collapse: collapse;
    margin: 0.7rem 0 0.3rem 0;
    font-size: 0.85rem;
  }
  
  th, td {
    border: 1px solid ${({ theme }) => theme.isDarkMode ? 'rgba(255,255,255,0.07)' : theme.colors.neutral[150]};
    padding: 0.26rem 0.33rem;
  }
  
  th {
    background: ${({ theme }) => theme.isDarkMode ? 'rgba(255,255,255,0.03)' : theme.colors.surface.paper};
    font-weight: 600;
  }
  
  tr:nth-child(even) { background: ${({ theme }) => theme.isDarkMode ? 'rgba(255,255,255,0.03)' : theme.colors.neutral[50]}; }
  tr:nth-child(odd) { background: transparent; }
`;

const Button = styled.button`
  padding: 0.38rem 0.82rem;
  border-radius: 4.5px;
  font-weight: 500;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s;
`;

const SecondaryButton = styled(Button)`
  background: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.1)' : theme.colors.surface.paper};
  color: ${({ theme }) => theme.isDarkMode ? '#FFFFFF' : theme.colors.neutral[900]};
  border: 1px solid ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.2)' : theme.colors.neutral[200]};
  opacity: ${props => props.disabled ? 0.5 : 0.9};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  &:hover {
    background: ${({ theme, disabled }) => disabled ? 'none' : theme.isDarkMode ? 'rgba(255, 255, 255, 0.2)' : theme.colors.surface.hover};
    border-color: ${({ theme, disabled }) => disabled ? theme.isDarkMode ? 'rgba(255, 255, 255, 0.2)' : theme.colors.neutral[200] : theme.isDarkMode ? 'rgba(255, 255, 255, 0.3)' : theme.colors.neutral[300]};
    opacity: ${props => props.disabled ? 0.5 : 1};
  }
`;

const PrimaryButton = styled(Button)`
  background: ${({ theme }) => theme.isDarkMode 
    ? 'linear-gradient(to right, #02987b, #00ffd0)'
    : theme.colors.primary.main};
  color: #FFFFFF;
  border: none;
  position: relative;
  overflow: hidden;
  
  &:hover {
    background: ${({ theme }) => theme.isDarkMode 
      ? 'linear-gradient(to right, #02987b, #00ffd0)'
      : theme.colors.primary.dark};
    transform: translateY(-1px);
    box-shadow: ${({ theme }) => theme.isDarkMode 
      ? '0 0 15px rgba(0, 255, 208, 0.3)'
      : 'none'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: ${({ theme }) => theme.isDarkMode 
      ? 'linear-gradient(to right, #02987b, #00ffd0)'
      : theme.colors.primary.main};
    transform: none;
    box-shadow: none;
    &:hover {
      background: ${({ theme }) => theme.isDarkMode 
        ? 'linear-gradient(to right, #02987b, #00ffd0)'
        : theme.colors.primary.main};
      transform: none;
    }
  }
`;

const ViewDetailsButton = styled(PrimaryButton)``;

const PhasedDescription = styled.p`
  color: inherit;
  opacity: 0.9;
  margin-bottom: 1rem;
`;

const SecondaryNote = styled.p`
  color: ${({ theme }) => theme.isDarkMode ? '#FFFFFF' : theme.colors.neutral[900]};
  opacity: ${({ theme }) => theme.isDarkMode ? 0.7 : 0.8};
  font-style: italic;
  margin-bottom: 0.75rem;
  font-size: 0.6875rem; // 11px
`;

const AcknowledgementButton = styled.button`
  width: 100%;
  background: ${({ theme }) => theme.isDarkMode 
    ? 'linear-gradient(to right, #02987b, #00ffd0)'
    : theme.colors.primary.main};
  color: #FFFFFF;
  padding: 0.48rem 0.85rem;
  border: none;
  border-radius: 0.29rem;
  cursor: pointer;
  font-weight: 500;
  font-size: 0.97rem;
  transition: all 0.2s;
  
  &:hover {
    background: ${({ theme }) => theme.isDarkMode 
      ? 'linear-gradient(to right, #02987b, #00ffd0)'
      : theme.colors.primary.dark};
    box-shadow: ${({ theme }) => theme.isDarkMode 
      ? '0 0 15px rgba(0, 255, 208, 0.3)'
      : 'none'};
  }
`;

const PdfButton = styled(PrimaryButton)`
  background: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.1)' : theme.colors.surface.paper};
  color: ${({ theme }) => theme.isDarkMode ? '#FFFFFF' : theme.colors.neutral[900]};
  border: 1px solid ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.2)' : theme.colors.neutral[200]};
  margin-left: 1rem;
  &:hover {
    background: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.2)' : theme.colors.surface.hover};
    border-color: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.3)' : theme.colors.neutral[300]};
  }
`;

const PDFContainer = styled.div`
  flex: 1;
  height: 60vh;
  background: ${({ theme }) => theme.isDarkMode ? theme.colors.neutral[900] : theme.colors.white};
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.isDarkMode ? theme.colors.neutral[700] : theme.colors.neutral[200]};

  iframe {
    border: none;
    background: ${({ theme }) => theme.isDarkMode ? theme.colors.neutral[900] : theme.colors.white};
  }
`;

const ViewToggleButton = styled(PrimaryButton)`
  background: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.1)' : theme.colors.surface.paper};
  color: ${({ theme }) => theme.isDarkMode ? '#FFFFFF' : theme.colors.neutral[900]};
  border: 1px solid ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.2)' : theme.colors.neutral[200]};
  &:hover {
    background: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.2)' : theme.colors.surface.hover};
    border-color: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.3)' : theme.colors.neutral[300]};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-left: auto;
`;

const CheckboxContainer = styled.div`
  margin: 1.5rem 0;
  background: ${({ theme }) => theme.isDarkMode ? '#1E1E1E' : theme.colors.neutral[50]};
  border-radius: 12px;
  padding: 1rem;
  border: 1px solid ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.1)' : theme.colors.neutral[200]};
`;

const CheckboxHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 0.75rem 0.75rem 0.75rem;
  border-bottom: 1px solid ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.1)' : theme.colors.neutral[200]};
  margin-bottom: 0.5rem;
`;

const SelectAllButton = styled.button`
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.isDarkMode ? '#00ffd0' : theme.colors.primary.main};
  font-size: 0.9rem;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) => theme.isDarkMode ? 'rgba(0, 255, 208, 0.1)' : theme.colors.surface.hover};
  }
`;

const CheckboxList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const CheckboxItem = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem;
  border-bottom: 1px solid ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.1)' : theme.colors.neutral[200]};
  color: ${({ theme }) => theme.isDarkMode ? '#FFFFFF' : theme.colors.neutral[900]};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.05)' : theme.colors.surface.paper};
    border-radius: 8px;
  }
`;

const TaskText = styled.span`
  color: ${({ theme }) => theme.isDarkMode ? '#FFFFFF' : theme.colors.neutral[900]};
`;

const CustomCheckbox = styled.label`
  display: inline-flex;
  align-items: center;
  cursor: pointer;

  input {
    display: none;
  }

  .checkbox-custom {
    width: 22px;
    height: 22px;
    border: 2px solid ${({ theme, checked }) => {
      if (theme.isDarkMode) {
        return checked ? '#00ffd0' : 'rgba(255, 255, 255, 0.2)';
      }
      return checked ? theme.colors.primary.main : theme.colors.neutral[300];
    }};
    border-radius: 6px;
    margin-left: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    background: ${({ theme, checked }) => {
      if (theme.isDarkMode) {
        return checked ? 'linear-gradient(to right, #02987b, #00ffd0)' : 'transparent';
      }
      return checked ? theme.colors.primary.main : 'transparent';
    }};
    position: relative;

    &:hover {
      border-color: ${({ theme }) => theme.isDarkMode ? '#00ffd0' : theme.colors.primary.main};
      background: ${({ theme, checked }) => {
        if (theme.isDarkMode) {
          return checked 
            ? 'linear-gradient(to right, #02987b, #00ffd0)'
            : 'rgba(0, 255, 208, 0.1)';
        }
        return checked ? theme.colors.primary.main : 'rgba(0, 0, 0, 0.05)';
      }};
    }

    &:after {
      content: "";
      position: absolute;
      width: 6px;
      height: 10px;
      border: solid #FFFFFF;
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
      opacity: ${({ checked }) => checked ? 1 : 0};
      transition: all 0.2s;
      margin-top: -2px;
    }
  }

  input:checked + .checkbox-custom {
    background: ${({ theme }) => theme.isDarkMode 
      ? 'linear-gradient(to right, #02987b, #00ffd0)'
      : theme.colors.primary.main};
    border-color: ${({ theme }) => theme.isDarkMode ? '#00ffd0' : theme.colors.primary.main};

    &:after {
      opacity: 1;
    }
  }
`;

// Add this component before DemoModal
const MarkdownWithLinks = ({ content }) => {
  const components = {
    a: ({ node, ...props }) => (
      <a
        {...props}
        onClick={(e) => {
          e.preventDefault();
          window.open(props.href, "_blank", "noopener,noreferrer");
        }}
      />
    ),
  };
  return (
    <ReactMarkdown components={components} remarkPlugins={[remarkGfm]}>
      {content}
    </ReactMarkdown>
  );
};

// Helper: remove all occurrences of ** and trim whitespace
const stripBold = (s) => s.replace(/\*\*/g, "").trim();

function parseMarkdownToData(markdown) {
  const lines = markdown.split("\n");
  const data = {
    question: "",
    awardee: "",
    employer: "",
    position: "",
    grantDate: "",
    totalRSUs: "",
    vestingSchedule: "",
    performanceBonus: "",
    planningConsiderations: [],
    taxationTable: [],
    recommendations: [],
  };

  let currentSection = "";

  lines.forEach((rawLine) => {
    const line = rawLine.trim();

    if (line.startsWith("## 📌 Summary Question")) {
      currentSection = "question";
      return;
    }
    if (line.startsWith("## 📄 Award Overview")) {
      currentSection = "overview";
      return;
    }
    if (line.startsWith("## 📊 Key Planning Considerations")) {
      currentSection = "planning";
      return;
    }
    if (line.startsWith("## 💰 Taxation Timing")) {
      currentSection = "taxation";
      return;
    }
    if (line.startsWith("## ✅ Actionable Recommendations")) {
      currentSection = "recommendations";
      return;
    }

    if (!line) return;

    switch (currentSection) {
      case "question":
        data.question = stripBold(line);
        break;

      case "overview":
        if (line.toLowerCase().startsWith("**awardee:**")) {
          const parts = line.split(":");
          data.awardee = stripBold(parts.slice(1).join(":"));
        }
        if (line.toLowerCase().startsWith("**employer:**")) {
          const parts = line.split(":");
          data.employer = stripBold(parts.slice(1).join(":"));
        }
        if (line.toLowerCase().startsWith("**position:**")) {
          const parts = line.split(":");
          data.position = stripBold(parts.slice(1).join(":"));
        }
        if (line.toLowerCase().startsWith("**grant date:**")) {
          const parts = line.split(":");
          data.grantDate = stripBold(parts.slice(1).join(":"));
        }
        if (line.toLowerCase().startsWith("**total rsus granted:**")) {
          const parts = line.split(":");
          data.totalRSUs = stripBold(parts.slice(1).join(":"));
        }
        if (line.toLowerCase().startsWith("**vesting schedule:**")) {
          const parts = line.split(":");
          data.vestingSchedule = stripBold(parts.slice(1).join(":"));
        }
        if (line.toLowerCase().startsWith("**performance bonus:**")) {
          const parts = line.split(":");
          data.performanceBonus = stripBold(parts.slice(1).join(":"));
        }
        break;

      case "planning":
        if (line.startsWith("-")) {
          const bulletText = stripBold(line.substring(2));
          data.planningConsiderations.push(bulletText);
        }
        break;

      case "taxation":
        if (line.startsWith("|") && !line.includes("---")) {
          const cells = line.split("|").filter((c) => c.trim());
          if (cells.length === 4) {
            data.taxationTable.push({
              event: cells[0].trim(),
              when: cells[1].trim(),
              impact: cells[2].trim(),
              type: cells[3].trim(),
            });
          }
        }
        break;

      case "recommendations":
        if (line.startsWith("|") && !line.includes("---")) {
          const cells = line.split("|").filter((c) => c.trim());
          if (cells.length === 2) {
            data.recommendations.push({
              item: cells[0].trim(),
              action: cells[1].trim(),
            });
          }
        }
        break;

      default:
        break;
    }
  });

  return data;
}

// Helper functions for parsing different modal types
function parseAlternativesSummary(summary) {
  // Get Fund Highlights
  const highlights = (summary.match(/\*\*Fund Highlights:\*\*([\s\S]*?)\n\n/) || [])[1] || "";
  // Get Summary
  const summaryText = (summary.match(/\*\*Summary:\*\*([\s\S]*?)\n\n/) || [])[1] || "";
  // Get Key Terms
  const keyTerms = (summary.match(/\*\*Key Terms:\*\*([\s\S]*)$/) || [])[1] || "";

  return {
    highlights: highlights.trim(),
    summary: summaryText.trim(),
    keyTerms: keyTerms.trim()
  };
}

function parseEstateSummary(summary) {
  // Extract sections using headers
  const keyStats = (summary.match(/### 📊 Key Statistics([\s\S]*?)(\n###|$)/) || [])[1] || "";
  const clientBenefits = (summary.match(/### 🎯 Client Benefits([\s\S]*?)(\n###|$)/) || [])[1] || "";
  const services = (summary.match(/### 💼 Our Services([\s\S]*?)(\n###|$)/) || [])[1] || "";
  const nextSteps = (summary.match(/### 📋 Next Steps([\s\S]*?)(\n###|$)/) || [])[1] || "";
  const discussionPoints = (summary.match(/### 💡 Discussion Points([\s\S]*)$/) || [])[1] || "";

  return {
    keyStats: keyStats.trim(),
    clientBenefits: clientBenefits.trim(),
    services: services.trim(),
    nextSteps: nextSteps.trim(),
    discussionPoints: discussionPoints.trim(),
  };
}

// ——— DemoModal Component ———

const DemoModal = ({
  isOpen,
  onClose,
  title,
  secondaryTitle = "",
  description,
  secondaryNote = "",
  phased = false,
  acknowledgementButtonLabel = "Got It",
  contextDetectionSummary,
  showNavigationButtons = false,
  showDismissButton = true,
  showSendLinkButton = false,
  showCreateTaskButton = false,
  showViewResearchButton = true,
  showAddReminderButton = false,
  disableAddReminder = false,
  showViewDetailsButton = false,
  showConfirmOnly = false,
  showConfirmButton = false,
  buttonLabel = "",
  targetPage = "",
  taskText = "",
  linkUrl = "",
  researchModalType = null,
  detailsModalType = null,
  researchSummary = "",
  researchTitle = "",
  externalUrl = null,
  taskList = [],
  completedTasks = [],
  onAction,
  demoId = 1,
  useInternalNavigation = false,
  customModalComponent = null,
  isAcknowledgmentModal = false,
  component = null,
  buttonText = "",
  dismissButtonLabel = "Dismiss",
  modalType = null,
  nextAction = null,
  spotlightTarget,
  spotlightLabel,
  disableDismiss = false,
  toolIntroduction,
  modalPhase,
  onAdvancePhase,
}) => {
  const { isDemoMode, onAction: demoAction, isConfirmingStop } = useDemo();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  // Debug layout measurements
  useEffect(() => {
    console.log('[DemoModal] Layout measurements:', {
      modalType,
      phase: modalPhase
    });
  }, [modalType, modalPhase]);

  // Move useEffect to the top level, before any conditional returns
  useEffect(() => {
    if (!isDemoMode || !modalType || !isOpen) return;

    const el = document.querySelector(`#modal-nav-btn-${modalType}`);
    if (!el) return;

    const event = new CustomEvent("spotlight", {
      detail: {
        target: el,
        ...(spotlightLabel || MODAL_TEMPLATES?.[modalType]?.spotlightLabel 
          ? { label: spotlightLabel || MODAL_TEMPLATES?.[modalType]?.spotlightLabel }
          : {}),
        disableAutoAdvance: true
      }
    });

    // Add short delay to let the DOM settle
    setTimeout(() => window.dispatchEvent(event), 300);
  }, [isDemoMode, modalType, isOpen, spotlightLabel]);

  // Add dismiss button spotlight effect
  useEffect(() => {
    if (!isDemoMode || !modalType || !isOpen) return;
    
    const template = MODAL_TEMPLATES[modalType];
    if (!template?.dismissSpotlightTarget) return;

    // Only fire in the *final* phase:
    //  • 2-phase modals → Phase 2
    //  • 3-phase modals → Phase 3
    const maxPhase = template.researchSummary?.trim().length ? 3 : 2;
    if (modalPhase !== maxPhase) return;
    
    const el = document.querySelector(template.dismissSpotlightTarget);
    if (!el) return;

    const event = new CustomEvent("spotlight", {
      detail: {
        target: el,
        ...(template.dismissSpotlightLabel ? { label: template.dismissSpotlightLabel } : {}),
        disableAutoAdvance: true
      }
    });

    setTimeout(() => window.dispatchEvent(event), 300);
  }, [isDemoMode, modalType, isOpen, modalPhase]);

  // No local phase state - only use props.modalPhase
  const [showPdfInline, setShowPdfInline] = useState(false);
  const [showDebriefingRoom, setShowDebriefingRoom] = useState(false);
  const [showDemoComplete, setShowDemoComplete] = useState(false);
  const [checked, setChecked] = useState(() => 
    taskList.map(task => !completedTasks.includes(task))
  );
  const [nextStepsChecked, setNextStepsChecked] = useState([]);
  const scrollWrapperRef = useRef(null);
  const reportBodyRef = useRef(null);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);

  // Debug modal lifecycle
  useEffect(() => {
    console.log("[DemoModal] Mounting with modalPhase:", modalPhase, "modalType:", modalType);
    return () => {
      console.warn("[DemoModal] Unmounting at modalPhase:", modalPhase, "modalType:", modalType);
    };
  }, [modalPhase, modalType]);

  // Fix 1: Add modalType to derived state
  const isMultiPhase = useMemo(() => phased === true, [phased]);
  const isThreePhase = useMemo(
    () => Boolean(phased && researchSummary && researchSummary.trim().length),
    [phased, researchSummary]
  );
  const tasks = useMemo(() => taskList || [], [taskList]);
  const currentModalType = useMemo(() => modalType || (MODAL_TEMPLATES[modalType]?.type), [modalType]);

  // Add phase 3 spotlight effect at the top level
  useEffect(() => {
    // Only run in phase 3 of three-phase modals
    if (!isOpen || !modalType || !isThreePhase || modalPhase !== 3) {
      return;
    }

    console.log('[DemoModal] Phase 3 mounted, checking for Add Reminder spotlight:', {
      modalType,
      template: MODAL_TEMPLATES[modalType],
      dismissSpotlightTarget: MODAL_TEMPLATES[modalType]?.dismissSpotlightTarget
    });

    const template = MODAL_TEMPLATES[modalType];
    if (!template?.dismissSpotlightTarget) {
      console.log('[DemoModal] No dismissSpotlightTarget found in template:', template);
      return;
    }

    const timeoutId = setTimeout(() => {
      const target = document.querySelector(template.dismissSpotlightTarget);
      console.log('[DemoModal] Looking for Add Reminder button:', {
        target,
        selector: template.dismissSpotlightTarget,
        buttonExists: Boolean(target)
      });

      if (!target) {
        console.warn('[DemoModal] Could not find Add Reminder button for spotlight:', template.dismissSpotlightTarget);
        return;
      }

      console.log('[DemoModal] Triggering spotlight for Add Reminder');
      window.dispatchEvent(new CustomEvent("spotlight", {
        detail: {
          target,
          ...(template.dismissSpotlightLabel ? { label: template.dismissSpotlightLabel } : {}),
          disableAutoAdvance: true
        }
      }));
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [isOpen, modalType, isThreePhase, modalPhase]);

  // Fix 2: Add debug logging for template and type
  useEffect(() => {
    if (showNavigationButtons && targetPage) {
      const actualModalType = currentModalType || modalType;
      console.log("🔍 Modal Debug:", {
        modalType: actualModalType,
        buttonId: `modal-nav-btn-${actualModalType || "default"}`,
        spotlightTarget,
        showNavigationButtons,
        targetPage,
        disableDismiss,
        template: MODAL_TEMPLATES[actualModalType],
        modalTypeRef: MODAL_TYPES[actualModalType],
        isMatch: MODAL_TYPES[actualModalType] === actualModalType
      });
    }
  }, [currentModalType, modalType, showNavigationButtons, targetPage, spotlightTarget, disableDismiss]);

  // Update spotlight activation effect with better timing and error handling
  useEffect(() => {
    if (!spotlightTarget) return;

    // Use MutationObserver to ensure button is mounted
    const observer = new MutationObserver((mutations, obs) => {
      const target = document.querySelector(spotlightTarget);
      if (target) {
        obs.disconnect(); // Stop observing once target is found
        window.dispatchEvent(new CustomEvent("spotlight", {
          detail: { 
            target,
            ...(spotlightLabel ? { label: spotlightLabel } : {})
          }
        }));
      }
    });

    // Start observing with a delay to ensure React has mounted components
    const timeoutId = setTimeout(() => {
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });

      // Fallback: try direct query after delay
      const target = document.querySelector(spotlightTarget);
      if (target) {
        observer.disconnect();
        window.dispatchEvent(new CustomEvent("spotlight", {
          detail: { 
            target,
            ...(spotlightLabel ? { label: spotlightLabel } : {})
          }
        }));
      }
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [spotlightTarget, spotlightLabel]);

  // Initialize task checkboxes
  useEffect(() => {
    if (isAcknowledgmentModal) return;
    const list = taskList || [];
    const template = MODAL_TEMPLATES[modalType];
    // Set all checkboxes to true if defaultChecked is true in the template
    if (list.length !== checked.length) {
      setChecked(list.map(() => template?.defaultChecked || false));
    }
    return () => {}; // Cleanup function
  }, [taskList, checked.length, isAcknowledgmentModal, modalType]);

  // Reset states when modal changes
  useEffect(() => {
    if (isAcknowledgmentModal) return;
    setShowPdfInline(false);
    setShowDebriefingRoom(false);
  }, [phased, researchSummary, isAcknowledgmentModal, isMultiPhase]);

  // Debug logging for Phase 3 rendering
  useEffect(() => {
    if (modalPhase === 3 && scrollWrapperRef.current && reportBodyRef.current) {
      const isScrollable = scrollWrapperRef.current.scrollHeight > scrollWrapperRef.current.clientHeight;
      
      console.group(`[DemoModal] Phase 3 Debug - ${modalType}`);
      console.log("[Render] ScrollWrapper rendered for modalPhase 3", {
        modalType,
        researchTitle: researchTitle || "No Title",
        hasContent: Boolean(researchSummary)
      });
      console.log("[Debug] isDemoMode =", isDemoMode);
      console.log("[Debug] Scroll Metrics:", {
        clientHeight: scrollWrapperRef.current.clientHeight,
        scrollHeight: scrollWrapperRef.current.scrollHeight,
        isScrollable
      });
      console.log("[Debug] ReportBody pointerEvents =", 
        window.getComputedStyle(reportBodyRef.current).pointerEvents
      );
      console.groupEnd();

      // Update scroll indicator based on content height
      setShowScrollIndicator(isScrollable);
    }
  }, [modalPhase, isDemoMode, modalType, researchSummary, researchTitle]);

  // Monitor scroll position for scroll indicator
  useEffect(() => {
    if (modalPhase === 3 && scrollWrapperRef.current) {
      const handleScroll = () => {
        const { scrollTop, scrollHeight, clientHeight } = scrollWrapperRef.current;
        const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;
        setShowScrollIndicator(!isAtBottom);
      };

      const scrollElement = scrollWrapperRef.current;
      scrollElement.addEventListener('scroll', handleScroll);
      handleScroll(); // Initial check

      return () => {
        scrollElement.removeEventListener('scroll', handleScroll);
      };
    }
  }, [modalPhase]);

  // Initialize next steps checkboxes when Estate Planning modal is shown
  useEffect(() => {
    if (
      isOpen && 
      (modalType === MODAL_TYPES.ESTATE_PLANNING || researchModalType === MODAL_TYPES.ESTATE_PLANNING)
    ) {
      const template = MODAL_TEMPLATES[modalType] || {};
      const parsed = parseEstateSummary(template.researchSummary);
      const nextSteps = parsed.nextSteps.split(/\n|•/).map(l => l.replace(/^[\s\-•\d.]+/, "").trim()).filter(Boolean);
      setNextStepsChecked(Array(nextSteps.length).fill(false));
    }
  }, [isOpen, modalType, researchModalType]);

  const handleClose = () => {
    onClose?.();
  };

  const handleAction = () => {
    // Get the current modal's template
    const template = MODAL_TEMPLATES[modalType] || {};
    
    console.log('[DemoModal] Handling primary action:', {
      modalType,
      nextModalType: template.nextModalType,
      template,
      modalTypeRef: MODAL_TYPES[modalType],
      isMatch: MODAL_TYPES[modalType] === modalType
    });

    // Special case: COMPLETE_DEMO should just run onAction and exit
    if (modalType === MODAL_TYPES.COMPLETE_DEMO) {
      onAction?.();
      return;
    }

    if (template.nextModalType) {
      // If we have a next modal type, dispatch that
      console.log('[DemoModal] Dispatching nextModalType:', template.nextModalType);
      onAction({ type: template.nextModalType });
    } else if (nextAction) {
      // Legacy support for nextAction
      console.log('[DemoModal] Using legacy nextAction:', nextAction);
      onAction(nextAction);
    } else {
      // Default behavior
      console.log('[DemoModal] No nextModalType or nextAction, using default onAction');
      onAction?.();
    }
  };

  const handleDebriefingRoomClose = () => {
    setShowDebriefingRoom(false);
    onClose?.();
  };

  const handleCompleteDemo = () => {
    handleClose();
  };

  const toggleTask = (idx) => {
    // Don't toggle if task is completed
    if (completedTasks.includes(taskList[idx])) return;
    
    const newChecked = [...checked];
    newChecked[idx] = !newChecked[idx];
    setChecked(newChecked);
  };

  const handleOpenSummary = async () => {
    setShowDebriefingRoom(true);
  };

  const handleReturnHome = () => {
    navigate('/');
    handleClose();
  };

  const handleRestartDemo = () => {
    handleClose();
    navigate('/');
  };

  // Helper dispatch: only fire the action, container will finish effect
  const dispatch = (action) => {
    onAction(action);
  };

  // Component detection hooks
  const templateComponent = useMemo(() => {
    if (!modalType) return null;
    const template = MODAL_TEMPLATES[modalType];
    console.log('[DemoModal] Looking up template component for:', modalType, template);
    // Check both component and customModalComponent fields
    return template?.component || template?.CustomComponent || customModalComponent;
  }, [modalType, customModalComponent]);

  // Debug logging effects
  useEffect(() => {
    if (modalType) {
      console.log('[DemoModal] Component detection:', {
        modalType,
        hasTemplateComponent: Boolean(templateComponent),
        hasCustomComponent: Boolean(customModalComponent),
        template: MODAL_TEMPLATES[modalType],
        component: templateComponent?.name || customModalComponent?.name,
        templateComponent,
        customModalComponent
      });
    }
  }, [modalType, templateComponent, customModalComponent]);

  // Check for components in order of precedence
  const FinalComponent = templateComponent;
  if (FinalComponent) {
    console.log('[DemoModal] Rendering component:', FinalComponent.name, {
      isOpen: true,
      onClose,
      modalType,
      template: MODAL_TEMPLATES[modalType]
    });
    return (
      <Modal 
        isOpen={true} 
        onClose={onClose} 
        width="medium"      // Default size for component modals (416px)
      >
        <FinalComponent 
          isOpen={true} 
          onClose={onClose}
          {...(MODAL_TEMPLATES[modalType] || {})}
        />
      </Modal>
    );
  }

  // --- Smart Focus Phase Logic ---
  const isSmartFocus = modalType === MODAL_TYPES.SMART_FOCUS;
  const isPhasedSmartFocus = isSmartFocus && phased && (toolIntroduction || modalPhase === 2);
  const currentPhase = typeof modalPhase === 'number' ? modalPhase : (isPhasedSmartFocus ? 1 : 2);
  
  // Debug Smart Focus detection
  if (isSmartFocus) {
    console.log('[DemoModal] Smart Focus detection:', { 
      isSmartFocus, 
      isPhasedSmartFocus, 
      phased, 
      toolIntroduction, 
      currentPhase,
      modalType,
      modalTypeRef: MODAL_TYPES.SMART_FOCUS,
      isMatch: modalType === MODAL_TYPES.SMART_FOCUS
    });
  }

  // Update AcknowledgementButton render with ID
  if (isMultiPhase && modalPhase === 1) {
    return (
      <Modal 
        isOpen={isOpen} 
        onClose={onClose} 
        width="small"       // Acknowledgment modals should be small (288px)
      >
        <Title>{title}</Title>
        <Description>{description}</Description>
        {secondaryNote && <SecondaryNote>{secondaryNote}</SecondaryNote>}
        <AcknowledgementButton 
          id={`modal-nav-btn-${modalType}`}
          onClick={onAdvancePhase || onClose}
        >
          {acknowledgementButtonLabel}
        </AcknowledgementButton>
      </Modal>
    );
  }
  
  // --- Non-Intro Smart Focus Logic ---
  if (isSmartFocus && !toolIntroduction) {
    // Show both Focus and Dismiss immediately (no intro phase)
    console.log('[DemoModal] Non-intro Smart Focus - showing Focus/Dismiss immediately');
    
    // Unified handler for advancing the demo (used by both Dismiss and X button)
    const handleAdvanceDemo = () => {
      console.log('[DemoModal] Smart Focus - advancing demo via dismiss/close');
      // Close modal and advance transcript
      if (onClose) onClose();
      // Also dispatch ADVANCE_DEMO to ensure transcript moves forward
      if (onAction) onAction({ type: 'ADVANCE_DEMO' });
    };
    
    const handleDismiss = handleAdvanceDemo;
    
    const handleFocus = () => {
      console.log('[DemoModal] Non-intro Smart Focus Focus clicked');
      if (onAction) onAction();
    };
    
    return (
      <Modal 
        isOpen={isOpen} 
        onClose={onClose} 
        width="medium"
      >
        <Title>{title}</Title>
        <Description>{description}</Description>
        <ButtonContainer>
          <SecondaryButton onClick={handleDismiss}>
            {dismissButtonLabel || "Dismiss"}
          </SecondaryButton>
          <PrimaryButton 
            id={`modal-action-btn-${modalType}`}
            onClick={handleFocus}
            className="spotlight-target primary-button"
            data-spotlight-target="true"
            data-modal-type={modalType}
          >
            {buttonLabel || "Focus"}
          </PrimaryButton>
        </ButtonContainer>
      </Modal>
    );
  }
  // --- End Non-Intro Smart Focus Logic ---

  // — PHASE 1 — only if multi-phase
  if (isMultiPhase && modalPhase === 1) {
    return (
      <Modal 
        isOpen={isOpen} 
        onClose={onClose} 
        width="medium"
      >
        <Title>{title}</Title>
        <Description>{description}</Description>
        {secondaryNote && <SecondaryNote>{secondaryNote}</SecondaryNote>}
        <AcknowledgementButton 
          id={`modal-nav-btn-${modalType}`}
          onClick={onAdvancePhase || onClose}
        >
          {acknowledgementButtonLabel}
        </AcknowledgementButton>
      </Modal>
    );
  }

  // — PHASE 2 — always show for phased (2-phase) or second step of 3-phase
  if (isMultiPhase && modalPhase === 2) {
    return (
      <Modal 
        isOpen={isOpen} 
        onClose={onClose} 
        width="medium"
      >
        <Title>{secondaryTitle || title}</Title>
        {description && (
          <Description style={{ marginBottom: 10 }}>
            {description}
          </Description>
        )}
        {secondaryNote && (
          <SecondaryNote>
            {secondaryNote}
          </SecondaryNote>
        )}
        {contextDetectionSummary && (
          <Description isItalic style={{ marginBottom: 10 }}>
            {contextDetectionSummary}
          </Description>
        )}

        {tasks.length > 0 && (
          <CheckboxContainer>
            <CheckboxHeader>
              <TaskText>Tasks</TaskText>
              <SelectAllButton onClick={() => {
                const nonCompletedTasks = taskList.filter(task => !completedTasks.includes(task));
                const allNonCompletedChecked = nonCompletedTasks.every((task, idx) => checked[idx]);
                setChecked(taskList.map(task => 
                  completedTasks.includes(task) ? false : !allNonCompletedChecked
                ));
              }}>
                {taskList.filter(task => !completedTasks.includes(task)).every((task, idx) => checked[idx]) 
                  ? 'Deselect All' 
                  : 'Select All'}
              </SelectAllButton>
            </CheckboxHeader>
            <CheckboxList>
              {tasks.map((task, idx) => (
                <CheckboxItem key={idx}>
                  <TaskText>{task}</TaskText>
                  <CustomCheckbox checked={checked[idx]}>
                    <input
                      type="checkbox"
                      checked={checked[idx]}
                      onChange={() => toggleTask(idx)}
                    />
                    <div className="checkbox-custom" />
                  </CustomCheckbox>
                </CheckboxItem>
              ))}
            </CheckboxList>
          </CheckboxContainer>
        )}

        <ButtonContainer>
          {showDismissButton && (
            <SecondaryButton 
              id={`modal-dismiss-btn-${modalType}`}
              onClick={onClose}
              disabled={disableDismiss}
            >
              {dismissButtonLabel || "Dismiss"}
            </SecondaryButton>
          )}

          {showViewResearchButton && (
            <PrimaryButton 
              id={`modal-research-btn-${modalType}`}
              onClick={() => {
                // For 3-phase modals with researchSummary, advance to Phase 3
                if (researchSummary && onAdvancePhase) {
                  console.log('[DemoModal] View Research clicked, advancing to Phase 3');
                  onAdvancePhase();
                } else {
                  // Fallback to onClose for 2-phase modals
                  onClose();
                }
              }}
              className="spotlight-target primary-button"
              data-spotlight-target="true"
              data-modal-type={modalType}
            >
              View Research
            </PrimaryButton>
          )}

          {showViewDetailsButton && detailsModalType && (
            <ViewDetailsButton onClick={() => dispatch({ type: detailsModalType })}>
              {buttonLabel || "View Details"}
            </ViewDetailsButton>
          )}

          {showAddReminderButton && (
            <PrimaryButton 
              id={`modal-add-reminder-btn-${modalType}`}
              className="spotlight-target primary-button"
              data-spotlight-target="true"
              data-modal-type={modalType}
              onClick={() => {
                demoAction({ type: "ADD_REMINDER" });
                onClose();
              }}
              disabled={disableAddReminder}
            >
              Add Reminder
            </PrimaryButton>
          )}

          {showSendLinkButton && (
            <PrimaryButton 
              id={`modal-send-link-btn-${modalType}`}
              onClick={() => {
                console.log('[DemoModal] Send Link clicked');
                const template = MODAL_TEMPLATES[modalType];
                if (template?.onSendLink) {
                  console.log('[DemoModal] Using nextModalType for Send Link:', template.nextModalType);
                  onAction({ 
                    type: template.nextModalType,
                    data: {
                      fromDemo: true,
                      householdId: 253,
                      householdMembers: [
                        { id: 1, name: 'Thomas Borer', email: 'thomas@example.com' },
                        { id: 2, name: 'Mary Borer', email: 'mary@example.com' }
                      ]
                    }
                  });
                } else {
                  console.log('[DemoModal] Using default Send Link action');
                  dispatch({ type: 'SEND_LINK' });
                }
              }}
            >
              Send Link
            </PrimaryButton>
          )}

          {showCreateTaskButton && (
            <PrimaryButton onClick={() => {
              dispatch({ type: "TASK_CREATION" });
              onClose();
            }}>
              Create Task
            </PrimaryButton>
          )}

          {(showNavigationButtons || showConfirmButton || showConfirmOnly) && (
            <PrimaryButton 
              onClick={
                showNavigationButtons
                  ? onAction
                  : showConfirmButton
                    ? onAction
                    : /* showConfirmOnly */ onClose
              }
              id={`modal-action-btn-${currentModalType || modalType}`}
              className="spotlight-target primary-button"
              data-spotlight-target="true"
              data-modal-type={currentModalType || modalType}
            >
              {buttonLabel || acknowledgementButtonLabel}
            </PrimaryButton>
          )}
        </ButtonContainer>
      </Modal>
    );
  }

  // — PHASE 3 — only if 3-phase
  if (isThreePhase && modalPhase === 3) {
    // Special-case: Use parsed grid/table only for RSU/Stock Options
    if (
      modalType === MODAL_TYPES.SALES_REMINDER_STOCK_OPTIONS ||
      researchModalType === MODAL_TYPES.SALES_REMINDER_STOCK_OPTIONS
    ) {
      const parsedData = parseMarkdownToData(researchSummary);

      // Parse View Documents URL from markdown
      let docUrl = null;
      let docLabel = "View Documents";
      const docMatch = researchSummary.match(/\[([^\]]*documents[^\]]*)\]\(([^)]+)\)/i);
      if (docMatch) {
        docLabel = docMatch[1] || docLabel;
        docUrl = docMatch[2];
      }

      const handleOpenSummary3 = async () => {
        if (externalUrl) {
          window.open(externalUrl, "_blank", "noopener,noreferrer");
        } else {
          const blob = await pdf(<RSUSummaryPDF data={parsedData} />).toBlob();
          const url = URL.createObjectURL(blob);
          window.open(url, "_blank", "noopener,noreferrer");
        }
      };

      return (
        <Modal 
          isOpen={isOpen} 
          onClose={onClose} 
          width="830px"
          maxHeight="dynamic"
        >
          <div style={{ padding: '0.65rem 0.9rem 0.65rem 0.9rem', minWidth: 540 }}>
            <ModalTopBar>
              <div style={{ minWidth: 0 }}>
                <div style={{
                  fontSize: "1.08rem",
                  fontWeight: 700,
                  margin: 0,
                  lineHeight: 1.18,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {researchTitle || "R&D Report: RSU Tax Impact Summary"}
                </div>
                <div style={{
                  fontSize: "0.96rem",
                  color: "#bfc1c4",
                  margin: 0,
                  fontWeight: 400,
                  marginBottom: 2
                }}>
                  {parsedData.question}
                </div>
              </div>
              <OpenSummaryButton
                style={{
                  marginLeft: 10,
                  marginTop: 1,
                  minWidth: 0,
                  fontSize: '0.78rem',
                  padding: "0.42rem 0.76rem"
                }}
                onClick={handleOpenSummary3}
              >
                Open in New Window
              </OpenSummaryButton>
            </ModalTopBar>

            <ScrollWrapper
              ref={scrollWrapperRef}
              showScrollIndicator={showScrollIndicator}
              style={{
                maxHeight: '57vh',
                minHeight: '34vh',
                margin: '0.15rem 0'
              }}
            >
              <TightReportBody
                ref={reportBodyRef}
                nonInteractive={isDemoMode}
                style={{
                  fontSize: '0.89rem',
                  padding: '0.58rem 0.7rem',
                  lineHeight: 1.33,
                  borderRadius: 8
                }}
              >
                <div className="top-sections">
                  {/* Award Overview */}
                  <div className="award-overview" style={{ paddingRight: 10 }}>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      marginBottom: 6
                    }}>
                      <div style={{ fontWeight: 600, marginTop: 0, fontSize: '0.95rem' }}>Award Overview</div>
                      {docUrl && (
                        <a
                          href={docUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            fontSize: "0.82rem",
                            fontWeight: 500,
                            color: "#46cefa",
                            textDecoration: "none",
                            padding: "0.2em 0.5em",
                            borderRadius: 4,
                            background: "rgba(70, 206, 250, 0.07)",
                          }}
                        >
                          {docLabel}
                        </a>
                      )}
                    </div>
                    <div style={{ marginBottom: 6 }}>
                      <span style={{ fontWeight: 600 }}>Awardee:</span> {parsedData.awardee}
                    </div>
                    <div style={{ marginBottom: 6 }}>
                      <span style={{ fontWeight: 600 }}>Employer:</span> {parsedData.employer}
                    </div>
                    <div style={{ marginBottom: 6 }}>
                      <span style={{ fontWeight: 600 }}>Position:</span> {parsedData.position}
                    </div>
                    <div style={{ marginBottom: 6 }}>
                      <span style={{ fontWeight: 600 }}>Grant Date:</span> {parsedData.grantDate}
                    </div>
                    <div style={{ marginBottom: 6 }}>
                      <span style={{ fontWeight: 600 }}>Total RSUs Granted:</span> {parsedData.totalRSUs}
                    </div>
                    <div style={{ marginBottom: 6 }}>
                      <span style={{ fontWeight: 600 }}>Vesting Schedule:</span> {parsedData.vestingSchedule}
                    </div>
                    <div>
                      <span style={{ fontWeight: 600 }}>Performance Bonus:</span> {parsedData.performanceBonus}
                    </div>
                  </div>
                  {/* Key Planning Considerations */}
                  <div className="key-planning" style={{ paddingLeft: 12 }}>
                    <div style={{ fontWeight: 600, marginBottom: 2, fontSize: '0.95rem' }}>Key Planning Considerations</div>
                    <ul style={{ margin: 0, paddingLeft: 17, listStyleType: 'disc' }}>
                      {parsedData.planningConsiderations.map((p, idx) => (
                        <li key={idx} style={{
                          marginBottom: '0.17em',
                          paddingLeft: 2,
                          fontSize: '0.92em',
                          lineHeight: 1.38
                        }}>
                          {p}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Divider */}
                <div style={{
                  borderTop: "1px solid #2a2a2a33",
                  margin: "0.18rem 0 0.23rem 0"
                }} />

                {/* Taxation Timing Table */}
                {parsedData.taxationTable?.length > 0 && (
                  <>
                    <div style={{
                      fontWeight: 600,
                      margin: "0.41rem 0 0.17rem 0",
                      fontSize: '0.95rem'
                    }}>Taxation Timing and Treatment</div>
                    <table>
                      <thead>
                        <tr>
                          <th>Event</th>
                          <th>When</th>
                          <th>Tax Impact</th>
                          <th>Tax Type</th>
                        </tr>
                      </thead>
                      <tbody>
                        {parsedData.taxationTable
                          .filter((row, i) => !(i === 0 && row.event === "Event" && row.when === "When"))
                          .map((row, i) => (
                            <tr key={i}>
                              <td>{row.event}</td>
                              <td>{row.when}</td>
                              <td>{row.impact}</td>
                              <td>{row.type}</td>
                            </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}

                {/* Divider */}
                {parsedData.recommendations?.length > 0 && (
                  <div style={{
                    borderTop: "1px solid #2a2a2a33",
                    margin: "0.18rem 0 0.18rem 0"
                  }} />
                )}

                {/* Actionable Recommendations */}
                {parsedData.recommendations?.length > 0 && (
                  <>
                    <div style={{
                      fontWeight: 600,
                      margin: "0.41rem 0 0.17rem 0",
                      fontSize: '0.95rem'
                    }}>Actionable Recommendations</div>
                    <table>
                      <thead>
                        <tr>
                          <th>Item</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {parsedData.recommendations
                          .filter((row, i) => !(i === 0 && row.item === "Item" && row.action === "Action"))
                          .map((row, i) => (
                            <tr key={i}>
                              <td>{row.item}</td>
                              <td>{row.action}</td>
                            </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}
              </TightReportBody>
            </ScrollWrapper>

            <ButtonContainer style={{ marginTop: '0.8rem', gap: '0.38rem' }}>
              <SecondaryButton onClick={handleClose}>Dismiss</SecondaryButton>
              <PrimaryButton
                id={`modal-add-reminder-btn-${modalType}`}
                onClick={() => {
                  demoAction({ type: "ADD_REMINDER" });
                  onClose();
                }}
                className="spotlight-target primary-button"
                data-spotlight-target="true"
                data-modal-type={modalType}
              >
                Add Reminder
              </PrimaryButton>
            </ButtonContainer>
          </div>
        </Modal>
      );
    } else if (
      modalType === MODAL_TYPES.SALES_REMINDER_ALTERNATIVES ||
      researchModalType === MODAL_TYPES.SALES_REMINDER_ALTERNATIVES
    ) {
      // Pull summary pieces
      const template = MODAL_TEMPLATES[modalType] || {};
      const parsed = parseAlternativesSummary(template.researchSummary);
      // Parse headline lines (name, manager/category)
      const summaryLines = template.researchSummary.split('\n');
      const fundNameLine = summaryLines[0] || '';
      const fundCategoryLine = summaryLines[1] || '';

      return (
        <Modal isOpen={isOpen} onClose={onClose} width="830px" maxHeight="dynamic">
          <div style={{ padding: '0.65rem 0.9rem 0.65rem 0.9rem', minWidth: 540 }}>
            <ModalTopBar>
              <div>
                <div style={{
                  fontSize: "1.08rem",
                  fontWeight: 700,
                  lineHeight: 1.18
                }}>
                  R&D Report: Horizon Growth Partners Fund I
                </div>
              </div>
              <OpenSummaryButton
                style={{
                  marginLeft: 10,
                  marginTop: 1,
                  minWidth: 0,
                  fontSize: '0.78rem',
                  padding: "0.42rem 0.76rem"
                }}
                onClick={() => window.open(template.externalUrl, "_blank", "noopener,noreferrer")}
              >
                Open in New Window
              </OpenSummaryButton>
            </ModalTopBar>
            <TightReportBody style={{ fontSize: '0.89rem', padding: 0 }}>
              <div className="top-sections" style={{ padding: "0.35rem 0.35rem 0 0.35rem" }}>
                <div style={{ paddingRight: 12 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.98rem', marginBottom: 4 }}>Fund Highlights</div>
                  <div style={{ fontWeight: 500, fontSize: '0.92rem', marginBottom: 2 }}>{fundNameLine}</div>
                  <div style={{ fontStyle: "italic", color: "#8fa5c1", fontSize: '0.88rem', marginBottom: 4 }}>{fundCategoryLine}</div>
                  <MarkdownWithLinks content={parsed.highlights} />
                </div>
                <div style={{ paddingLeft: 12 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.98rem', marginBottom: 4 }}>Key Terms</div>
                  <MarkdownWithLinks content={parsed.keyTerms} />
                </div>
              </div>
              {/* Full width summary below columns */}
              {parsed.summary && (
                <div style={{
                  borderTop: "1px solid #2226",
                  margin: "0.33rem 0 0.1rem 0",
                  padding: "0.35rem 0.3rem 0.09rem 0.3rem",
                  fontSize: "0.94rem"
                }}>
                  <div style={{ fontWeight: 600, marginBottom: 2 }}>Summary</div>
                  <MarkdownWithLinks content={parsed.summary} />
                </div>
              )}
            </TightReportBody>
            <ButtonContainer style={{ marginTop: '0.8rem', gap: '0.38rem' }}>
              <SecondaryButton onClick={handleClose}>Dismiss</SecondaryButton>
              <PrimaryButton
                id={`modal-add-reminder-btn-${modalType}`}
                onClick={() => {
                  demoAction({ type: "ADD_REMINDER" });
                  onClose();
                }}
                className="spotlight-target primary-button"
                data-spotlight-target="true"
                data-modal-type={modalType}
              >
                Add Reminder
              </PrimaryButton>
            </ButtonContainer>
          </div>
        </Modal>
      );
    } else if (
      modalType === MODAL_TYPES.ESTATE_PLANNING ||
      researchModalType === MODAL_TYPES.ESTATE_PLANNING
    ) {
      const template = MODAL_TEMPLATES[modalType] || {};
      const parsed = parseEstateSummary(template.researchSummary);
      // Parse next steps as checklist
      const nextSteps = parsed.nextSteps.split(/\n|•/).map(l => l.replace(/^[\s\-•\d.]+/, "").trim()).filter(Boolean);
      const toggleStep = idx => setNextStepsChecked(c => c.map((v, i) => i === idx ? !v : v));

      return (
        <Modal isOpen={isOpen} onClose={onClose} width="830px" maxHeight="dynamic">
          <div style={{ padding: '0.65rem 0.9rem 0.65rem 0.9rem', minWidth: 540 }}>
            <ModalTopBar>
              <div>
                <div style={{
                  fontSize: "1.08rem",
                  fontWeight: 700,
                  lineHeight: 1.18
                }}>
                  R&D Report: Estate Planning Services
                </div>
              </div>
              <OpenSummaryButton
                style={{
                  marginLeft: 10,
                  marginTop: 1,
                  minWidth: 0,
                  fontSize: '0.78rem',
                  padding: "0.42rem 0.76rem"
                }}
                onClick={() => window.open("/financial-planning/estate-planning", "_blank")}
              >
                Open in New Window
              </OpenSummaryButton>
            </ModalTopBar>
            <TightReportBody style={{ fontSize: '0.89rem', padding: 0 }}>
              <div className="top-sections" style={{ padding: "0.35rem 0.35rem 0 0.35rem" }}>
                <div style={{ paddingRight: 12 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.98rem', marginBottom: 4 }}>Key Statistics</div>
                  <MarkdownWithLinks content={parsed.keyStats} />
                  <div style={{ fontWeight: 600, fontSize: '0.98rem', marginTop: 16, marginBottom: 4 }}>Client Benefits</div>
                  <MarkdownWithLinks content={parsed.clientBenefits} />
                </div>
                <div style={{ paddingLeft: 12 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.98rem', marginBottom: 4 }}>Our Services</div>
                  <MarkdownWithLinks content={parsed.services} />
                  <div style={{ fontWeight: 600, fontSize: '0.98rem', marginTop: 16, marginBottom: 4 }}>Discussion Points</div>
                  <MarkdownWithLinks content={parsed.discussionPoints} />
                </div>
              </div>
              {/* Full-width Next Steps checklist below columns */}
              {nextSteps.length > 0 && (
                <div style={{
                  borderTop: "1px solid #2226",
                  margin: "0.33rem 0 0.13rem 0",
                  padding: "0.35rem 0.3rem 0.09rem 0.3rem",
                  fontSize: "0.94rem"
                }}>
                  <div style={{ fontWeight: 600, marginBottom: 2 }}>Next Steps</div>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {nextSteps.map((step, i) => (
                      <li key={i} style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 3 }}>
                        <input
                          type="checkbox"
                          checked={nextStepsChecked[i]}
                          onChange={() => toggleStep(i)}
                          style={{ accentColor: "#04f0b6", width: 18, height: 18 }}
                        />
                        <span>{step}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </TightReportBody>
            <ButtonContainer style={{ marginTop: '0.8rem', gap: '0.38rem' }}>
              <SecondaryButton onClick={handleClose}>Dismiss</SecondaryButton>
              <PrimaryButton
                id={`modal-add-reminder-btn-${modalType}`}
                onClick={() => {
                  demoAction({ type: "ADD_REMINDER" });
                  onClose();
                }}
                className="spotlight-target primary-button"
                data-spotlight-target="true"
                data-modal-type={modalType}
              >
                Add Reminder
              </PrimaryButton>
            </ButtonContainer>
          </div>
        </Modal>
      );
    } else {
      // For all other research modals, render as dense Markdown
      return (
        <Modal 
          isOpen={isOpen} 
          onClose={onClose} 
          width="830px"
          maxHeight="dynamic"
        >
          <div style={{ padding: '0.65rem 0.9rem 0.65rem 0.9rem', minWidth: 540 }}>
            <ModalTopBar>
              <div style={{ minWidth: 0 }}>
                <div style={{
                  fontSize: "1.08rem",
                  fontWeight: 700,
                  margin: 0,
                  lineHeight: 1.18,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {researchTitle || title || "Research"}
                </div>
              </div>
              {externalUrl && (
                <OpenSummaryButton
                  style={{
                    marginLeft: 10,
                    marginTop: 1,
                    minWidth: 0,
                    fontSize: '0.78rem',
                    padding: "0.42rem 0.76rem"
                  }}
                  onClick={() => window.open(externalUrl, "_blank", "noopener,noreferrer")}
                >
                  Open in New Window
                </OpenSummaryButton>
              )}
            </ModalTopBar>
            <ScrollWrapper
              ref={scrollWrapperRef}
              showScrollIndicator={showScrollIndicator}
              style={{
                maxHeight: '57vh',
                minHeight: '34vh',
                margin: '0.15rem 0'
              }}
            >
              <ReportBody ref={reportBodyRef} nonInteractive={isDemoMode}>
                <MarkdownWithLinks content={researchSummary} />
              </ReportBody>
            </ScrollWrapper>
            <ButtonContainer style={{ marginTop: '0.8rem', gap: '0.38rem' }}>
              <SecondaryButton onClick={handleClose}>Dismiss</SecondaryButton>
              {showAddReminderButton && (
                <PrimaryButton
                  id={`modal-add-reminder-btn-${modalType}`}
                  onClick={() => {
                    demoAction({ type: "ADD_REMINDER" });
                    onClose();
                  }}
                  className="spotlight-target primary-button"
                  data-spotlight-target="true"
                  data-modal-type={modalType}
                >
                  Add Reminder
                </PrimaryButton>
              )}
            </ButtonContainer>
          </div>
        </Modal>
      );
    }
  }

  // COMPLETE_DEMO rendering
  if (showConfirmOnly && detailsModalType === MODAL_TYPES.COMPLETE_DEMO) {
    console.log('[DemoModal] Rendering COMPLETE_DEMO modal:', {
      modalType,
      detailsModalType,
      template: MODAL_TEMPLATES[MODAL_TYPES.COMPLETE_DEMO],
      nextModalType: MODAL_TEMPLATES[MODAL_TYPES.COMPLETE_DEMO]?.nextModalType
    });
    
    return (
      <Modal 
        isOpen={isOpen} 
        onClose={onClose} 
        width="small"       // Confirmation modals should be small (288px)
      >
        <Title>{title}</Title>
        {description && <Description>{description}</Description>}
        {secondaryNote && <SecondaryNote>{secondaryNote}</SecondaryNote>}
        <AcknowledgementButton
          onClick={() => {
            console.log("COMPLETE_DEMO main button clicked! Firing handleAction...");
            handleAction();
          }}
          className="btn-primary w-full"
        >
          {acknowledgementButtonLabel}
        </AcknowledgementButton>
      </Modal>
    );
  }

  if (isConfirmingStop) return null;

  // — DEFAULT (non‐phased) —
  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      width="medium"
    >
      <Title>{title}</Title>
      {description && <Description>{description}</Description>}
      {secondaryNote && <SecondaryNote>{secondaryNote}</SecondaryNote>}

      {taskList.length > 0 && (
        <CheckboxContainer>
          <CheckboxHeader>
            <TaskText>Tasks</TaskText>
            <SelectAllButton onClick={() => {
              const allChecked = checked.every(Boolean);
              setChecked(taskList.map(() => !allChecked));
            }}>
              {checked.every(Boolean) ? 'Deselect All' : 'Select All'}
            </SelectAllButton>
          </CheckboxHeader>
          <CheckboxList>
            {taskList.map((task, idx) => (
              <CheckboxItem key={idx}>
                <TaskText>{task}</TaskText>
                {completedTasks.includes(task) ? (
                  <span className="px-2 py-0.5 text-[11px] font-medium bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full flex items-center gap-1">
                    <span>Complete</span>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </span>
                ) : (
                  <CustomCheckbox checked={checked[idx]}>
                    <input
                      type="checkbox"
                      checked={checked[idx]}
                      onChange={() => toggleTask(idx)}
                    />
                    <div className="checkbox-custom" />
                  </CustomCheckbox>
                )}
              </CheckboxItem>
            ))}
          </CheckboxList>
        </CheckboxContainer>
      )}

      <ButtonContainer>
        {showDismissButton && (
          <SecondaryButton
            onClick={onClose}
            disabled={disableDismiss}
          >
            {dismissButtonLabel || "Dismiss"}
          </SecondaryButton>
        )}
        {showCreateTaskButton && (
          <PrimaryButton 
            id={`modal-create-task-btn-${modalType}`}
            onClick={() => {
              dispatch({ type: "TASK_CREATION" });
              onClose();
            }}
            className="spotlight-target primary-button"
            data-spotlight-target="true"
            data-modal-type={modalType}
          >
            {buttonLabel || "Create Tasks"}
          </PrimaryButton>
        )}
        {showAddReminderButton && (
          <PrimaryButton
            id={`modal-add-reminder-btn-${modalType}`}
            className="spotlight-target primary-button"
            data-spotlight-target="true"
            data-modal-type={modalType}
            onClick={() => {
              demoAction({ type: "ADD_REMINDER" });
              onClose();
            }}
            disabled={disableAddReminder}
          >
            Add Reminder
          </PrimaryButton>
        )}
        {showViewDetailsButton && detailsModalType && (
          <ViewDetailsButton onClick={() => dispatch({ type: detailsModalType })}>
            {buttonLabel || "View Details"}
          </ViewDetailsButton>
        )}
        {showNavigationButtons && targetPage && (
          <PrimaryButton
            id={`modal-nav-btn-${currentModalType || modalType || "default"}`}
            onClick={onAction}
            className="spotlight-target primary-button"
            data-spotlight-target="true"
            data-modal-type={currentModalType || modalType}
          >
            {buttonLabel || acknowledgementButtonLabel}
          </PrimaryButton>
        )}
        {showConfirmOnly && (
          <AcknowledgementButton
            id={`modal-nav-btn-${modalType}`}
            onClick={onClose}
          >
            {acknowledgementButtonLabel}
          </AcknowledgementButton>
        )}
      </ButtonContainer>
    </Modal>
  );
}

DemoModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  secondaryTitle: PropTypes.string,
  description: PropTypes.string,
  secondaryNote: PropTypes.string,
  phased: PropTypes.bool,
  acknowledgementButtonLabel: PropTypes.string,
  contextDetectionSummary: PropTypes.string,
  showNavigationButtons: PropTypes.bool,
  showDismissButton: PropTypes.bool,
  showSendLinkButton: PropTypes.bool,
  showCreateTaskButton: PropTypes.bool,
  showViewResearchButton: PropTypes.bool,
  showAddReminderButton: PropTypes.bool,
  disableAddReminder: PropTypes.bool,
  showViewDetailsButton: PropTypes.bool,
  showConfirmOnly: PropTypes.bool,
  buttonLabel: PropTypes.string,
  targetPage: PropTypes.string,
  taskText: PropTypes.string,
  linkUrl: PropTypes.string,
  researchModalType: PropTypes.string,
  detailsModalType: PropTypes.string,
  researchSummary: PropTypes.string,
  researchTitle: PropTypes.string,
  externalUrl: PropTypes.string,
  taskList: PropTypes.array,
  completedTasks: PropTypes.array,
  onAction: PropTypes.func.isRequired,
  demoId: PropTypes.number,
  useInternalNavigation: PropTypes.bool,
  customModalComponent: PropTypes.elementType,
  isAcknowledgmentModal: PropTypes.bool,
  component: PropTypes.elementType,
  buttonText: PropTypes.string,
  dismissButtonLabel: PropTypes.string,
  modalType: PropTypes.string,
  nextAction: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.arrayOf(PropTypes.object)
  ]),
  spotlightTarget: PropTypes.string,
  spotlightLabel: PropTypes.string,
  disableDismiss: PropTypes.bool,
  toolIntroduction: PropTypes.bool,
  modalPhase: PropTypes.number,
  onAdvancePhase: PropTypes.func,
};

export default DemoModal;
